import React, { Component } from 'react';
import './MapViewer.css';
import Img from '../../StableImage';
import { Tooltip } from 'reactstrap';

const FLOOR_SIZE = 16;

export default class MapViewer extends Component {

  constructor(props) {
    super(props);
    this.grid = React.createRef();
    this.savedPreview = this.props.run.plan;
    let plan = this.lastFloorPath(this.props.run.rooms).map(room => (room.model.model || room.model).index).concat(this.props.run.plan);
    this.state = {
      plan: plan,
      preview: plan
    }
  }

  getCollectableImg (reward) {

    switch (reward) {
    case "card": return "/images/card.png";
    case "coin": return "/images/coin.png";
    case "seed": return "/images/seed.png";
    case "berry": return "/images/berry.png";
    case "shards": return "/images/soulshard.png";
    case "runes": return "/images/rune.png";
    case "remove": return "/images/remove.png";
    case "relic": return "/images/relic2.png";
    case "soul": return "/images/soul.png";
    case "insight": return "/images/insight.png";
    case "paws": return "/images/paws.png";
    case "key": return "/images/key.png";
    }
  }

  componentDidMount() {
    const el = this.grid.current;
    if (el) {
      const scrollvalue = this.props.run.time / (FLOOR_SIZE+1);
      el.scrollTo({
        top: scrollvalue * (el.scrollHeight - el.clientHeight),
        behavior: 'auto' // or 'smooth' if you prefer animation
      });
    }
  }

  lastFloorPath(arr, size=(FLOOR_SIZE+1)) {

    arr = arr.filter(room => {
      const model = room.model.model || room.model;
      return model.index !== undefined;
    });
    let remainder = arr.length % size;
    let keep = remainder === 0 ? size : remainder;

    return arr.slice(-keep);
  }

  preview (grid, endRow, endIndex, visited) {

    let model = this.props.run.room.model;
    if (model.model)
      model = model.model;
    let startingRow = FLOOR_SIZE - this.props.run.time;
    let startingIndex = model.index;
    // Recalculate the hover route while preserving as much as possible of
    // the route established by the previous intentional room hover.
    let path = this.findPath(grid, startingRow, startingIndex, endRow, endIndex, this.savedPreview) || [];
    if (path)
      this.savedPreview = path;
    this.setState({preview: visited.concat(path)})
  }

  setPlan (e, grid, endRow, endIndex, visited) {

    let model = this.props.run.room.model;
    if (model.model)
      model = model.model;
    let startingRow = FLOOR_SIZE - this.props.run.time;
    let startingIndex = model.index;
    let path = this.findPath(grid, startingRow, startingIndex, endRow, endIndex, this.savedPreview) || [];
    if (path) {
      this.savedPreview = path;
      this.props.setPlan(path);
      this.setState({plan: visited.concat(path)})
    }
    e.stopPropagation();
  }

  findPath(grid, startingRow, startingIndex, endRow, endIndex, former = []) {
    const numRows = grid.length;
    if (numRows === 0) return null;
    if (endRow <= startingRow) return null;
    const numCols = grid[0].length;

    const inBounds = (r, c) => r >= 0 && r < numRows && c >= 0 && c < numCols;
    const getRoom = (r, c) => (inBounds(r, c) ? grid[r][c] : null);

    // Validate start & end rooms exist
    const startRoom = getRoom(startingRow, startingIndex);
    const endRoom = getRoom(endRow, endIndex);
    if (!startRoom || !endRoom) return null;

    // Same-row cases: no row-to-row movement possible
    if (startingRow === endRow) {
      return startingIndex === endIndex ? [] : null;
    }

    // Direction: +1 means going down (to higher row indices), -1 means going up
    const dir = endRow > startingRow ? 1 : -1;
    const rowsBetween = Math.abs(endRow - startingRow);

    function neighborsDown(row, col) {
      const room = getRoom(row, col);
      if (!room || !Array.isArray(room.next)) return [];
      return room.next
        .slice()
        .sort((a, b) => a - b)
        .filter((nextCol) => getRoom(row + 1, nextCol));
    }

    function neighborsUp(row, col) {
      const prevRow = row - 1;
      if (prevRow < 0) return [];
      const out = [];
      for (let c = 0; c < numCols; c++) {
        const parent = getRoom(prevRow, c);
        if (parent && Array.isArray(parent.next) && parent.next.includes(col)) {
          out.push(c);
        }
      }
      return out.sort((a, b) => a - b);
    }

    const getNeighbors = (row, col) => (dir === 1 ? neighborsDown(row, col) : neighborsUp(row, col));

    /**
     * Try to "advance" from (startingRow, startingIndex) by following a prefix path of columns.
     * Returns {row, col, path} where path is the prefix (copied), or null if the prefix is invalid.
     * Also rejects prefixes that overshoot endRow or step onto null rooms.
     */
    function advanceByPrefix(prefixCols) {
      // Cannot take more steps than rows available to end
      if (prefixCols.length > rowsBetween) return null;

      let row = startingRow;
      let col = startingIndex;
      const pathSoFar = [];

      for (let i = 0; i < prefixCols.length; i++) {
        const nextRow = row + dir;
        if (!inBounds(nextRow, col)) return null;

        const allowed = getNeighbors(row, col);
        const targetCol = prefixCols[i];

        // Step must be to the adjacent row and into an allowed column that exists
        if (!allowed.includes(targetCol)) return null;

        // Move
        row = nextRow;
        col = targetCol;
        pathSoFar.push(col);
      }

      // If we already hit the end (prefix exactly reaches end row & col), we’re done
      if (row === endRow && col === endIndex) {
        return { row, col, path: pathSoFar };
      }

      // If we've passed the end row (shouldn't happen due to rowsBetween check), reject
      // Otherwise, return the partial state for continuation search
      return { row, col, path: pathSoFar };
    }

    /**
     * BFS from a given state to end, returning the completed path (including the state's path prefix).
     * Deterministic: explores neighbors in ascending column order.
     */
    function continueSearch(fromState) {
      if (!fromState) return null;

      const queue = [fromState];
      const visited = new Set([`${fromState.row},${fromState.col}`]);

      while (queue.length) {
        const { row, col, path } = queue.shift();

        if (row === endRow && col === endIndex) {
          return path;
        }

        const nextRow = row + dir;
        // Stop if we can’t go further in this direction
        if (!inBounds(nextRow, 0)) continue;

        const nbrs = getNeighbors(row, col);
        for (const nCol of nbrs) {
          const key = `${nextRow},${nCol}`;
          if (!visited.has(key)) {
            visited.add(key);
            queue.push({ row: nextRow, col: nCol, path: [...path, nCol] });
          }
        }
      }
      return null;
    }

    // Try with the longest possible prefix of `former`, then progressively truncate
    const maxTry = Math.min(former.length, rowsBetween);
    for (let k = maxTry; k >= 0; k--) {
      const prefix = former.slice(0, k); // do not mutate the original
      const state = advanceByPrefix(prefix);
      const result = continueSearch(state);
      if (result) return result;
    }

    return null;
  }

  render () {

    const grid = this.props.map.sections;

    const shiftX = 10 * (7 - this.props.run.room.x);
    const shiftY = 10 * (7 - this.props.run.room.y);

    let visited = this.lastFloorPath(this.props.run.rooms).map(room => (room.model.model || room.model).index);

    let model = this.props.run.room.model;
    if (model.model)
      model = model.model;

    return (
      <div className="card-dropper map-viewer">
        <div className="lightbox-container" onClick={() => this.props.onClose()}>
          <div className="lightbox-inner">
            <div className="map-grid" ref={this.grid}>
            <div key="map-row-top" className="map-row"><div className="map-cell"/></div>
            <div key="map-row-top2" className="map-row"><div className="map-cell"/></div>
            {
              [...grid].reverse().map((row, i) =>
                <div key={"map-row-" + i} className="map-row" style={{transform: "translateX(" + shiftX + "vh) translateY(" + shiftY + "vh)"}}>
                {
                  row.map((room, j) =>
                    <div key={"map-cell-" + i + "-" + j} className="map-cell">
                    {
                      room && 
                      <div className={"map-room map-room-" + room.status + (room.locked ? " map-room-locked" : "") + (visited[grid.length-1-i] === room.index ? " map-room-visited" : (this.state.plan[grid.length-1-i] === room.index ? " map-room-planned" : (this.state.preview[grid.length-1-i] === room.index ? " map-room-previewed" : "")))}
                      onMouseEnter={() => this.preview(grid, FLOOR_SIZE-i, room.index, visited)} onMouseLeave={() => this.setState({preview: []})} onClick={e => this.setPlan(e, grid, FLOOR_SIZE-i, room.index, visited)}>
                      { room.status !== "unknown" &&
                        <div className={"map-" + room.type}>
                        {
                          (()=>{

                            switch (room.type) {
                            case "start": return;
                            case "event": if (room.status !== "visited") return <Img src="/images/unknown.png"/>; return <Img src={room.model.icon}/>;
                            case "fight": return <Img src="/images/fight.png"/>;
                            case "rival": return <Img src="/images/rival.png"/>;
                            case "boss": return <Img src="/images/boss.png"/>;
                            case "event": return <Img src="/images/unknown.png"/>;
                            case "treasure": return <Img src="/images/treasure.png"/>;
                            //case "chest": if (room.status !== "visited") return; return <Img className={!room.treasure || room.treasure.relic ? "" : "map-inactive"} src="/images/chest.png"/>;
                            case "shop": return <Img src="/images/shop.png"/>;
                            case "menagerie": return <Img src="/images/menagerie.png"/>;
                            case "fountain": return <Img src="/images/fountain.png"/>;
                            case "amaryllis": return <Img src="/images/amaryllis.png"/>;
                            case "aurora": return <Img src="/images/aurora.png"/>;
                            case "nikara": return <Img src="/images/nikara.png"/>;
                            case "zephyrus": return <Img src="/images/zephyrus.png"/>;
                            case "chest": return <Img src="/images/chest.png"/>;
                            }

                          })()
                        }
                          <div className="map-room-collectables">
                          {
                            (room.type === "fight" || room.type === "boss") && room.reward &&
                            (Array.isArray(room.reward) ? room.reward : [room.reward]).map((reward, k) => 
                              <div key={i + "-reward-" + k} className="map-room-collectable">
                                <Img key={"map-room-collectable-" + this.getCollectableImg(reward)} src={this.getCollectableImg(reward)}/>
                              </div>
                            )
                          }
                          </div>
                        {
                          model.index === room.index && this.props.run.time === i && <Img className="map-champion" src={"/images/chibi/" + this.props.run.champion.model.name + ".png"}/>
                        }
                        {
                          room.locked && <Img className="map-lock" src={"/images/key.png"}/>
                        }
                        <div className="map-room-paths">
                        {
                          room.next.map(next => <div key={"path-" + i + "-" + j + "-" + next} className={"map-room-path " + (visited[grid.length-1-i] === room.index && visited[grid.length-i] === next ? "map-room-path-visited " : (this.state.plan[grid.length-1-i] === room.index && this.state.plan[grid.length-i] === next ? "map-room-path-planned " : (this.state.preview[grid.length-1-i] === room.index && this.state.preview[grid.length-i] === next ? "map-room-path-previewed " : ""))) + (next === room.index ? "forward" : (next < room.index ? "left" : "right"))}/>)
                        }
                        </div>
                        </div>
                      }
                      </div>
                    }
                    </div>
                  )
                }
                </div>
              )
            }
            <div key="map-row-bottom" className="map-row"><div className="map-cell"/></div>
            <div key="map-row-bottom2" className="map-row"><div className="map-cell"/></div>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

